import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import spotlightService from '../services/SpotlightService';
import { cacheService } from '../services/CacheService';
import episodeSelectionService from '../services/EpisodeSelectionService';
import userDataService from '../services/UserDataService';
import PDPModal from './PDPModal';
import ProgressBar from './common/ProgressBar';
import './ChannelCard.css';
import { getPosterSize } from '../config/categoryRailSettings';

const ChannelCard = React.memo(
  ({ channel = {}, style, index, onHoverModalShow, onHoverModalHide, externalClickHandle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Ensure channel is always an object, even if it's undefined or null
    const safeChannel = channel || {};

    // Check if this is a top-number style rail
    const isTopNumber = style === 'top-number' || style === 'top-number-poster';
    const isPosterFormat = style === 'top-number-poster' || style === 'poster';
    const isContinueWatching = style === 'continue-watching';

    // Get the original image URL based on style
    let originalImageUrl;
    if (isPosterFormat) {
      // Use poster format for top-number-poster
      originalImageUrl =
        safeChannel.poster ||
        safeChannel.spotlight_poster ||
        (safeChannel.image && (safeChannel.image.poster || safeChannel.image.spotlight_poster));
    } else {
      // Use 16:9 format for regular and top-number
      originalImageUrl =
        safeChannel.spotlight_poster ||
        safeChannel.poster ||
        (safeChannel.image && (safeChannel.image.spotlight_poster || safeChannel.image.poster));
    }

    // Manually construct the image URL with desired dimensions
    let imageUrl;
    if (originalImageUrl) {
      if (originalImageUrl.startsWith('http')) {
        // If it's a full URL, append the sizing parameters
        const url = new URL(originalImageUrl);
        const imageId = url.pathname.split('/').pop();
        if (isPosterFormat) {
          // Poster dimensions (2:3 aspect ratio) - higher quality for display
          imageUrl = `https://dprodspmedia.cachefly.net/images/${imageId}/320/480?webp=1`;
        } else {
          // 16:9 dimensions - higher quality for carousel display
          imageUrl = `https://dprodspmedia.cachefly.net/images/${imageId}/480/270?webp=1`;
        }
      } else {
        // If it's just an ID, construct the full URL
        if (isPosterFormat) {
          // Poster dimensions (2:3 aspect ratio) - higher quality for display
          imageUrl = `https://dprodspmedia.cachefly.net/images/${originalImageUrl}/320/480?webp=1`;
        } else {
          // 16:9 dimensions - higher quality for carousel display
          imageUrl = `https://dprodspmedia.cachefly.net/images/${originalImageUrl}/480/270?webp=1`;
        }
      }
    } else {
      // Fallback for missing images
      if (isPosterFormat) {
        imageUrl = 'https://via.placeholder.com/320x480?text=No+Image';
      } else {
        imageUrl = 'https://via.placeholder.com/480x270?text=No+Image';
      }
    }

    const title = safeChannel.title || safeChannel.name || 'Untitled Channel';

    // State for tracking image load errors
    const [imageError, setImageError] = useState(false);

    // State for PDP modal
    const [showPDPModal, setShowPDPModal] = useState(false);
    const [pdpVideoState, setPdpVideoState] = useState({ autoPlay: false, currentTime: 0 });

    // State for progress data
    const [progressData, setProgressData] = useState(null);
    const [progressLoading, setProgressLoading] = useState(false);

    const cardRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    // Handle image load errors
    const handleImageError = () => {
      console.warn(`Failed to load image: ${imageUrl}`);
      setImageError(true);
    };

    // Determine if this is a movie or series based on channel properties
    // First check programming_type as requested by the user
    const isMovie =
      safeChannel.programming_type === 'Movie' ||
      safeChannel.type === 'movie' ||
      safeChannel.content_type === 'movie';

    const isSeries =
      safeChannel.programming_type === 'Series' ||
      safeChannel.type === 'series' ||
      safeChannel.content_type === 'series';

    // Use channel slug instead of ID for navigation
    const channelSlug =
      safeChannel.slug || safeChannel.permalink || safeChannel._id || safeChannel.id;

    // Create the permalink path and navigate directly
    const channelPath = `/movies?channel=${channelSlug}`;

    // Handle click on channel card - open PDP modal instead of navigation
    const handleClick = () => {
      if (externalClickHandle) return externalClickHandle();

      // Always open PDP modal on click
      setShowPDPModal(true);
    };

    // Fetch progress data for series
    const { isAuthenticated } = useAuth0();

    useEffect(() => {
      const fetchProgressData = async () => {
        // Only fetch for authenticated users, series, AND continue watching rail
        if (!isAuthenticated || !isSeries || !safeChannel._id || !isContinueWatching) {
          setProgressData(null);
          return;
        }

        setProgressLoading(true);

        try {
          // Use O(1) lookup to get the latest resumption for this channel
          const latestResumption = userDataService.getLatestResumption(safeChannel._id);

          if (!latestResumption) {
            setProgressData(null);
            return;
          }

          // Format the progress data for ProgressBar component
          const formattedProgress = {
            title: latestResumption.episode.title,
            episodeNumber: latestResumption.episode.episode,
            seasonNumber: latestResumption.episode.channel?.season || 1,
            progress: latestResumption.progress,
            point: latestResumption.point,
            duration: latestResumption.duration,
            isSeries: true,
          };

          setProgressData(formattedProgress);
        } catch (error) {
          console.error('ChannelCard: Error fetching progress data:', error);
          setProgressData(null);
        } finally {
          setProgressLoading(false);
        }
      };

      fetchProgressData();
    }, [isAuthenticated, isSeries, isContinueWatching, safeChannel._id]);

    // Advanced modal positioning configuration
    const MODAL_POSITIONING = {
      // Base offsets (adjust these values to fine-tune positioning)
      offsetX: -20, // Horizontal offset in pixels (negative = left, positive = right)
      offsetY: 0, // Vertical offset in pixels (negative = up, positive = down)

      // Style-specific offsets
      styleOffsets: {
        'top-number-poster': {
          offsetX: -100, // Different offset for poster format
          offsetY: -10,
        },
        'top-number': {
          offsetX: -15, // Different offset for top-number style
          offsetY: 5,
        },
        default: {
          offsetX: -40, // Default offset for regular cards
          offsetY: -20,
        },
      },

      // Responsive offsets based on screen size
      responsiveOffsets: {
        mobile: {
          // Screen width <= 768px
          offsetX: -10,
          offsetY: 0,
        },
        tablet: {
          // Screen width <= 1024px
          offsetX: -15,
          offsetY: 0,
        },
        desktop: {
          // Screen width > 1024px
          offsetX: -20,
          offsetY: 0,
        },
      },

      // Position anchoring options
      anchor: {
        horizontal: 'left', // 'left', 'center', 'right' - where to anchor horizontally on the card
        vertical: 'top', // 'top', 'center', 'bottom' - where to anchor vertically on the card
      },

      // Viewport boundary handling
      boundaryHandling: {
        enabled: true, // Enable automatic boundary detection
        padding: 20, // Minimum distance from viewport edges
        fallbackToCenter: false, // If true, center modal when it would go off-screen
      },

      // Debug mode - set to true to see positioning info in console
      debug: true,
    };

    // Advanced modal position calculation with multiple positioning options
    const calculateModalPosition = cardElement => {
      if (!cardElement) return { x: 0, y: 0 };

      const rect = cardElement.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // Get current style for style-specific offsets
      const currentStyle = style || 'default';

      // Get responsive offset based on screen size
      let responsiveOffset = MODAL_POSITIONING.responsiveOffsets.desktop;
      if (viewport.width <= 768) {
        responsiveOffset = MODAL_POSITIONING.responsiveOffsets.mobile;
      } else if (viewport.width <= 1024) {
        responsiveOffset = MODAL_POSITIONING.responsiveOffsets.tablet;
      }

      // Get style-specific offset
      const styleOffset =
        MODAL_POSITIONING.styleOffsets[currentStyle] || MODAL_POSITIONING.styleOffsets.default;

      // Calculate base position based on anchor points
      let baseX, baseY;

      // Horizontal anchoring
      switch (MODAL_POSITIONING.anchor.horizontal) {
        case 'center':
          baseX = rect.left + rect.width / 2;
          break;
        case 'right':
          baseX = rect.right;
          break;
        case 'left':
        default:
          baseX = rect.left;
          break;
      }

      // Vertical anchoring
      switch (MODAL_POSITIONING.anchor.vertical) {
        case 'center':
          baseY = rect.top + rect.height / 2;
          break;
        case 'bottom':
          baseY = rect.bottom;
          break;
        case 'top':
        default:
          baseY = rect.top;
          break;
      }

      // Apply offsets (priority: style-specific > responsive > base)
      const finalOffsetX =
        styleOffset.offsetX !== undefined
          ? styleOffset.offsetX
          : responsiveOffset.offsetX !== undefined
          ? responsiveOffset.offsetX
          : MODAL_POSITIONING.offsetX;

      const finalOffsetY =
        styleOffset.offsetY !== undefined
          ? styleOffset.offsetY
          : responsiveOffset.offsetY !== undefined
          ? responsiveOffset.offsetY
          : MODAL_POSITIONING.offsetY;

      let finalX = baseX + finalOffsetX;
      let finalY = baseY + finalOffsetY;

      // Boundary handling
      if (MODAL_POSITIONING.boundaryHandling.enabled) {
        const modalWidth = 416; // Approximate modal width
        const modalHeight = 400; // Approximate modal height
        const padding = MODAL_POSITIONING.boundaryHandling.padding;

        // Check horizontal boundaries
        if (finalX < padding) {
          if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
            finalX = (viewport.width - modalWidth) / 2;
          } else {
            finalX = padding;
          }
        } else if (finalX + modalWidth > viewport.width - padding) {
          if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
            finalX = (viewport.width - modalWidth) / 2;
          } else {
            finalX = viewport.width - modalWidth - padding;
          }
        }

        // Check vertical boundaries
        if (finalY < padding) {
          if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
            finalY = (viewport.height - modalHeight) / 2;
          } else {
            finalY = padding;
          }
        } else if (finalY + modalHeight > viewport.height - padding) {
          if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
            finalY = (viewport.height - modalHeight) / 2;
          } else {
            finalY = viewport.height - modalHeight - padding;
          }
        }
      }

      // Debug logging disabled

      return { x: finalX, y: finalY };
    };

    // Handle hover modal show - with 1000ms delay
    const handleHoverModalShow = useCallback(() => {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Set 600ms delay before showing modal
      hoverTimeoutRef.current = setTimeout(() => {
        if (cardRef.current && onHoverModalShow) {
          const position = calculateModalPosition(cardRef.current);
          onHoverModalShow(safeChannel, position, index);
        }
      }, 600);
    }, [safeChannel, onHoverModalShow, index]);

    // Handle hover modal hide - immediate, clear timeout
    const handleHoverModalHide = useCallback(() => {
      // Clear the timeout if user stops hovering before 600ms
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (onHoverModalHide) {
        onHoverModalHide();
      }
    }, [onHoverModalHide]);

    // Handle opening PDP modal from hover modal
    const handleOpenPDPModal = useCallback(
      videoState => {
        setPdpVideoState(videoState);
        setShowPDPModal(true);
        if (onHoverModalHide) {
          onHoverModalHide(); // Close hover modal
        }
      },
      [onHoverModalHide],
    );

    // Set up hover prefetching (keep existing functionality)
    const { handleMouseEnter: handlePrefetchEnter, handleMouseLeave: handlePrefetchLeave } =
      useHoverPrefetch(channelSlug, isSeries ? 'series' : 'channel', 500);

    // Combined mouse enter handler
    const handleMouseEnter = useCallback(() => {
      handlePrefetchEnter();
      handleHoverModalShow();
    }, [handlePrefetchEnter, handleHoverModalShow]);

    // Combined mouse leave handler
    const handleMouseLeave = useCallback(() => {
      handlePrefetchLeave();
      handleHoverModalHide();
    }, [handlePrefetchLeave, handleHoverModalHide]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    // Check if this channel has the recently added label
    const hasRecentlyAddedLabel = safeChannel.custom_fields?.card_label === 'recently_added';

    // Debug logging for custom fields
    if (safeChannel.slug === 'johnny-316' || safeChannel.permalink === 'johnny-316') {
      // Debug logging removed
    }

    // Get poster size from config if poster format or top-number-card
    const posterSize = isPosterFormat || isTopNumber ? getPosterSize(window.innerWidth) : null;

    return (
      <>
        <div
          ref={cardRef}
          className={`flex-shrink-0 cursor-pointer channel-card ${
            isPosterFormat ? 'w-64' : 'w-88'
          } ${isTopNumber ? 'top-number-card' : ''}`}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="relative overflow-hidden rounded-lg">
            {/* Number Overlay for top-number styles */}
            {isTopNumber && (
              <div className="absolute top-0 left-0 z-10 number-overlay">
                <span className="number-text">{index + 1}</span>
              </div>
            )}

            {/* Recently Added Label */}
            {hasRecentlyAddedLabel && (
              <div className="absolute z-20 px-2 py-1 text-xs font-semibold text-white rounded-md shadow-lg top-2 right-2 bg-accent">
                Recently Added
              </div>
            )}

            <img
              src={
                imageError
                  ? isPosterFormat
                    ? 'https://via.placeholder.com/300x450?text=No+Image'
                    : 'https://via.placeholder.com/300x169?text=No+Image'
                  : imageUrl
              }
              alt={title}
              style={
                isTopNumber ? { width: posterSize?.width, height: posterSize?.height } : undefined
              }
              className={`object-cover w-full transition-transform duration-300 hover:scale-105 ${
                isPosterFormat ? 'h-90' : 'h-45'
              }`}
              onError={handleImageError}
              loading="lazy"
            />
            {/* Progress Overlay - Show for Continue Watching rails */}
            {isContinueWatching && (
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                {/* Use resumption_data if available, otherwise use progressData */}
                {(safeChannel.resumption_data ||
                  (isSeries && progressData && !progressLoading)) && (
                  <ProgressBar
                    progressData={
                      safeChannel.resumption_data
                        ? {
                            title: safeChannel.parent_channel_title
                              ? safeChannel.episode_title
                              : safeChannel.title,
                            episodeNumber: (() => {
                              // For series in continue watching, try to get correct episode info
                              if (safeChannel.parent_channel_id) {
                                try {
                                  const seriesId = safeChannel.parent_channel_id;
                                  const continueWatchingInfo =
                                    episodeSelectionService.getContinueWatchingEpisode(
                                      seriesId,
                                      safeChannel.childchannels,
                                    );
                                  if (continueWatchingInfo && continueWatchingInfo.episode) {
                                    return (
                                      continueWatchingInfo.episode.episode_number ||
                                      continueWatchingInfo.episode.episode ||
                                      1
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    'ChannelCard: Error getting correct episode info:',
                                    error,
                                  );
                                }
                              }
                              // Fallback to resumption data
                              return (
                                safeChannel.resumption_data.episodeNumber ||
                                safeChannel.resumption_data.channel?.episode
                              );
                            })(),
                            seasonNumber: (() => {
                              // For series in continue watching, try to get correct season info
                              if (safeChannel.parent_channel_id) {
                                try {
                                  const seriesId = safeChannel.parent_channel_id;
                                  const continueWatchingInfo =
                                    episodeSelectionService.getContinueWatchingEpisode(
                                      seriesId,
                                      [],
                                    );
                                  if (continueWatchingInfo && continueWatchingInfo.season) {
                                    return (
                                      continueWatchingInfo.season.season_number ||
                                      continueWatchingInfo.season.season ||
                                      1
                                    );
                                  }
                                } catch (error) {
                                  console.error(
                                    'ChannelCard: Error getting correct season info:',
                                    error,
                                  );
                                }
                              }
                              // Fallback to resumption data
                              return (
                                safeChannel.resumption_data.seasonNumber ||
                                safeChannel.resumption_data.channel?.season ||
                                1
                              );
                            })(),
                            progress: safeChannel.resumption_data.progress,
                            point: safeChannel.resumption_data.point,
                            duration: safeChannel.resumption_data.duration,
                            isSeries: !!safeChannel.parent_channel_id,
                            showResumeText: true,
                          }
                        : progressData
                    }
                    variant="card"
                    size="small"
                    showProgressBar={true}
                    showEpisodeInfo={!!safeChannel.parent_channel_id || isSeries}
                    className="text-white"
                  />
                )}
              </div>
            )}

            {/* Simplified hover overlay - detailed info now in modal */}
          </div>
          <div className="hidden mt-2">
            <h3 className="text-sm font-medium text-white truncate">{title}</h3>
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-400 truncate" title={originalImageUrl}>
                {originalImageUrl?.substring(0, 30)}...
              </div>
            )}
          </div>
        </div>

        {/* PDP Modal */}
        {showPDPModal && (
          <PDPModal
            channel={safeChannel}
            isVisible={showPDPModal}
            onClose={() => setShowPDPModal(false)}
            autoPlayVideo={pdpVideoState.autoPlay}
            videoCurrentTime={pdpVideoState.currentTime}
          />
        )}
      </>
    );
  },
);

export default ChannelCard;

// Custom hook for hover prefetching
const useHoverPrefetch = (channelSlug, type, delay = 300) => {
  const timeoutRef = useRef(null);
  const hasPrefetched = useRef(false);

  const handleMouseEnter = useCallback(() => {
    if (hasPrefetched.current || !channelSlug) return;

    timeoutRef.current = setTimeout(async () => {
      try {
        // Check if already cached to avoid redundant requests
        const cacheKey = `channel_${channelSlug}`;
        if (cacheService.get(cacheKey)) {
          return;
        }

        await spotlightService.getChannelBySlug(channelSlug).catch(e => {
          throw new Error(e);
        });

        hasPrefetched.current = true;
      } catch (error) {
        console.warn(`Prefetch failed for channel ${channelSlug}:`, error);
      }
    }, delay);
  }, [channelSlug, delay]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { handleMouseEnter, handleMouseLeave };
};
